import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import toast, { Toaster } from "react-hot-toast";

const WorkTypesPage = () => {
  const [workTypeData, setWorkTypeData] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  // 1. Updated formData state to match the required structure
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    subType: [],
  });
  const [isEditMode, setIsEditMode] = useState(false);

  const columnHelper = createColumnHelper();

  // 2. Updated columns to display the new data fields
  const columns = useMemo(
    () => [
      columnHelper.accessor("key", {
        header: () => "key",
        cell: (info) => <span className="font-mono">{info.getValue()}</span>,
      }),
      columnHelper.accessor("value", {
        header: () => "Work Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("subType", {
        header: () => "Subtypes",
        // Join the array for clean display in the table
        cell: (info) => info.getValue()?.join(", ") || "N/A",
      }),
      columnHelper.accessor("actions", {
        header: () => "Actions",
        cell: (info) => (
          <div className="flex gap-2">
            <button
              onClick={() => handleEdit(info.row.original)}
              className="btn btn-primary btn-xs"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(info.row.original.key)}
              className="btn btn-error btn-xs"
            >
              Delete
            </button>
          </div>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: workTypeData,
    columns,
    state: {
      globalFilter: searchFilter,
    },
    onGlobalFilterChange: setSearchFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // 3. Renamed functions and updated API endpoints to /workType
  const getWorkTypeList = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      const response = await axios.get(`/workType`, { headers });
      setWorkTypeData(response.data.data);
    } catch (error) {
      console.error("Error fetching work type data:", error);
      toast.error("Error fetching work type data.");
    }
  };

  const handleSubmit = async () => {
    // 4. Added validation for the key field on create
    const idRegex = /^[a-zA-Z0-9_]+$/;
    if (!isEditMode && !idRegex.test(formData.key)) {
      toast.error(
        "key must not contain spaces or special characters (only letters, numbers, and underscores)."
      );
      return;
    }
    if (!formData.value) {
      toast.error("Work Name is required.");
      return;
    }

    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      if (isEditMode) {
        // On update, we send the full object back
        await axios.put(`/workType/${formData.key}`, formData, {
          headers,
        });
        toast.success("Work Type updated successfully!");
      } else {
        await axios.post(`/workType`, formData, { headers });
        toast.success("Work Type created successfully!");
      }
      getWorkTypeList();
      resetForm();
    } catch (error) {
      console.error("Error creating/updating work type:", error);
      toast.error(`Failed to ${isEditMode ? "update" : "create"} work type.`);
    }
  };

  const handleEdit = (workType) => {
    setIsEditMode(true);
    // 5. Populate the form state with the full object for editing
    setFormData({
      key: workType.key,
      value: workType.value,
      subType: workType.subType || [], // Ensure subType is always an array
    });
    document.getElementById("addWorkTypeModel").showModal();
  };

  const handleDelete = async (workTypeId) => {
    if (!window.confirm("Are you sure you want to delete this work type?")) {
      return;
    }

    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      await axios.delete(`/workType/${workTypeId}`, { headers });
      toast.success("Work Type deleted successfully!");
      getWorkTypeList();
    } catch (error) {
      console.error("Error deleting work type:", error);
      toast.error("Failed to delete work type.");
    }
  };

  const resetForm = () => {
    setFormData({ key: "", value: "", subType: [] });
    setIsEditMode(false);
    document.getElementById("addWorkTypeModel").close();
  };

  // --- Dynamic Form Handlers for Subtypes ---
  const handleSubTypeChange = (index, value) => {
    const newSubTypes = [...formData.subType];
    newSubTypes[index] = value;
    setFormData({ ...formData, subType: newSubTypes });
  };

  const addSubType = () => {
    setFormData({ ...formData, subType: [...formData.subType, ""] });
  };

  const removeSubType = (index) => {
    const newSubTypes = formData.subType.filter((_, i) => i !== index);
    setFormData({ ...formData, subType: newSubTypes });
  };

  useEffect(() => {
    getWorkTypeList();
  }, [searchFilter]);

  return (
    <div className={"flex"}>
      <div className="w-[90vw] ml-[7vw] mt-[2vh] mr-0 overflow-auto rounded-lg shadow-md">
        <div className="flex justify-between items-center p-4">
          <h1 className="text-2xl font-bold">Work Types</h1>
          <button
            onClick={() =>
              document.getElementById("addWorkTypeModel").showModal()
            }
            className="btn btn-primary"
            style={{ background: "limegreen", color: "white" }}
          >
            <span className="text-bold text-xl"> + </span> Add Work Type
          </button>
        </div>

        {/* 6. A completely new form for Work Types */}
        <dialog id="addWorkTypeModel" className="modal">
          <div className="modal-box w-11/12 max-w-2xl">
            <h3 className="font-bold text-lg mb-4">
              {isEditMode ? "Edit Work Type" : "Create Work Type"}
            </h3>

            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text">
                  Key (No spaces or special chars)
                </span>
              </label>
              <input
                type="text"
                placeholder="e.g., CUSTOM_SIGNAGE"
                className="input input-bordered w-full font-mono"
                value={formData.key}
                onChange={(e) =>
                  setFormData({ ...formData, key: e.target.value })
                }
                readOnly={isEditMode} // Prevent changing ID on edit
              />
            </div>
            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text">Work Name</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Custom Signage"
                className="input input-bordered w-full"
                value={formData.value}
                onChange={(e) =>
                  setFormData({ ...formData, value: e.target.value })
                }
              />
            </div>

            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text">Subtypes</span>
                <span className="label-text mr-24">Rate/sq.ft</span>
              </label>
              {formData.subType.map((sub, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder={`Subtype ${index + 1}`}
                    className="input input-bordered w-full"
                    value={sub}
                    onChange={(e) => handleSubTypeChange(index, e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder={`Rate`}
                    className="input input-bordered w-full max-w-[130px]"
                    value={"800"}
                    onChange={(e) => handleSubTypeChange(index, e.target.value)}
                  />
                  <button
                    onClick={() => removeSubType(index)}
                    className="btn btn-sm btn-circle btn-outline btn-error"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                onClick={addSubType}
                className="btn btn-outline btn-sm mt-2"
              >
                + Add Subtype
              </button>
            </div>

            <div className="modal-action">
              <button className="btn btn-primary" onClick={handleSubmit}>
                {isEditMode ? "Update" : "Create"}
              </button>
              <button className="btn" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </div>
        </dialog>

        <div className="p-4">
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Search by Key or Work Name..."
            className="input input-bordered w-full"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="text-left p-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-100 border-b">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 whitespace-nowrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between p-4">
          <span className="text-sm text-gray-700">
            Page{" "}
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </span>
          <div className="join">
            <button
              className="join-item btn"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              «
            </button>
            <button
              className="join-item btn"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              ‹
            </button>
            <button
              className="join-item btn"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              ›
            </button>
            <button
              className="join-item btn"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              »
            </button>
          </div>
          <select
            className="select select-bordered"
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default WorkTypesPage;
